import { Button, type ButtonRootProps } from "@heroui/react";
import { type AppButtonVariant, type AppButtonSize } from "./variants";

/**
 * Constrained button props.
 * Variant and size are restricted to the design system's allowed values.
 * className escape hatch available for one-off overrides.
 */
export type AppButtonProps = Omit<
  ButtonRootProps,
  "className" | "variant" | "size"
> & {
  /** Visual variant. @default "primary" */
  variant?: AppButtonVariant;
  /** Size. @default "md" */
  size?: AppButtonSize;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppButton — Design system button.
 *
 * Wraps HeroUI v3 Button with constrained Rosé Pine variants.
 * Styling is controlled exclusively through `variant` and `size` props.
 *
 * @example
 * ```tsx
 * <AppButton variant="primary" size="md" onPress={handleSave}>
 *   Save
 * </AppButton>
 * ```
 */
export function AppButton({
  variant = "primary",
  size = "md",
  className,
  ...props
}: AppButtonProps) {
  return <Button {...props} variant={variant} size={size} className={className} />;
}
